import { useQuery, useMutation } from '@apollo/client/react';
import React, { useCallback, useEffect, useState } from 'react';
import './vos.scss';
import { DELETE_VOS_UPLOAD_IMAGE, VOS_UPLOAD_IMAGE } from '../../../graphql/client/mutations';
import {
    GetVosUploadImages,
    GetVosUploadsByWeek,
    GetVosUploadImages_getVosUploadImages as JobImagesList,
    VosUploadImage,
    VosUploadImageInput,
    GetFullJobProfile,
} from '../../../types/schemaTypes';
import { GET_JOB_PROFILE, GET_VOS_UPLOAD_IMAGES, GET_VOS_UPLOADS_BY_WEEKS } from '../../../graphql/client/queries';
import { Error, Loading } from '../../_common';
import { ToastContainer, toast } from 'react-toastify';
import { toastOpt } from '../../../utils/helpers/toastHelper';
import { Col } from 'react-bootstrap';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import SearchableDropdown, { DropdownOption } from '../../_common/SearchableDropdown/SearchableDropdown';
import VosImageList from './VosImageList';
import { useParams } from 'react-router-dom';
import JobMenuItems from '../../Header/JobMenuItemsHeader/JobMenuItems';

const ErrorMessages = {
    delete: 'Failed to delete image',
};

const UploadErrorMessages = {
    upload: 'Failed to upload image.',
};

const Messages = {
    delete: 'Image deleted successfully',
};

const SuccessMessage = {
    upload: 'Image uploaded successfully',
};

const VosForTab = ({ }: any): React.ReactElement | null => {
    const jobId = parseInt(useParams().jobId ?? '');
    const [vosImg, setvosImg] = useState<any>('');
    const currentYear = new Date().getFullYear();
    const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(currentMonth);
    const [weeks, setWeeks] = useState<DropdownOption[]>([]);
    const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(currentYear);
    const defaultWeekOption = { id: -1, name: '--Select--' };
    
    const canViewEditVos = useAuthContext().userHasPermission(PermissionEnum.ViewVOS);
    const canUploadVos = useAuthContext().userHasPermission(PermissionEnum.UploadVOS);

    const generateYearsArray = (): DropdownOption[] => {
        const yearsArray: DropdownOption[] = [];
        yearsArray.push({ id: currentYear, name: `${currentYear}` });
        yearsArray.push({ id: currentYear - 1, name: `${currentYear - 1}` });
        return yearsArray;
    };

    const generateMonthsArray = (): DropdownOption[] => {
        const months: DropdownOption[] = [];
        for (let i = 1; i <= 12; i++) {
            const monthName = new Date(2000, i - 1).toLocaleString('default', { month: 'short' });
            months.push({ id: i, name: monthName });
        }
        return months;
    };

    const handleMonthSelect = (monthName: string | null) => {
        if (monthName !== null) {
            const monthNumber = parseInt(monthName, 10);
            setSelectedMonth(monthNumber);
            setSelectedWeek(-1);
        }
    };

    const handleYearSelect = (year: string | null) => {
        if (year !== null) {
            const yearAsInt = parseInt(year, 10);
            setSelectedYear(yearAsInt);
        }
    };

    const handleWeekSelect = (week: string | null) => {
        if (week === '--Select--') {
            setSelectedWeek(-1);
        }
        if (week !== null) {
            const weekAsInt = parseInt(week, 10);
            setSelectedWeek(weekAsInt);
        }
    };

    const months = generateMonthsArray();
    const years = generateYearsArray();
    const handleLoadFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (!event || !event.target || !event.target.files) return;
        const file = event.target.files[0];
        setvosImg(file);
        event.target.value = '';
    };

    const generateWeeks = (weeksData: GetVosUploadsByWeek | undefined): void => {
        if (weeksData && weeksData.getVosUploadsByWeek) {
            const weeksOptions = weeksData.getVosUploadsByWeek.map((week: any) => ({
                id: week.id,
                name: `${week.name}`,
            }));
            setWeeks([{ id: -1, name: '--Select--' }, ...weeksOptions]);
        } else {
            setWeeks([]);
        }
    };

    const {
        error: weekError,
        data: weeksData,
        loading: weeksLoading,
    } = useQuery<GetVosUploadsByWeek>(GET_VOS_UPLOADS_BY_WEEKS, {
        variables: {
            input: {
                year: selectedYear || '',
                monthId: selectedMonth || 0,
            },
        },
        fetchPolicy: 'network-only',
        skip: !canViewEditVos,
    });

    const { error, data, loading } = useQuery<GetVosUploadImages>(GET_VOS_UPLOAD_IMAGES, {
        variables: {
            input: {
                jobId: jobId,
                year: selectedYear,
                monthId: selectedMonth,
                weekId: -1,
            },
        },
        fetchPolicy: 'network-only',
        skip: !selectedYear || !selectedMonth,
    });

    const images: JobImagesList[] = [];
    if (data && data.getVosUploadImages) {
        data.getVosUploadImages.forEach((element: JobImagesList | null): void => {
            if (element !== null) {
                if (!selectedWeek || selectedWeek == -1) {
                    images.push(element);
                } else if (element?.week?.split('(')[0].split(' ')[1] == selectedWeek?.toString()) {
                    images.push(element);
                }
            }
        });
    }

    useEffect(() => {
        generateWeeks(weeksData);
    }, [weeksData]);

    const onCompleted = (action) => {
        toast.success(Messages[action], toastOpt);
    };

    const onUploadError = (action) => {
        toast.error(UploadErrorMessages[action], toastOpt);
    };

    const onActionError = (action) => {
        toast.error(ErrorMessages[action], toastOpt);
    };

    const onUploadCompleted = (action) => {
        toast.success(SuccessMessage[action], toastOpt);
    };

    const [uploadImageMutation, { loading: mutationLoading }] = useMutation<VosUploadImage>(VOS_UPLOAD_IMAGE, {
        onCompleted: (data) => {
            const updateImageErrors = data.vosUploadImage?.errors ?? [];
            if (updateImageErrors.length > 0) {
                onUploadError('upload');
            } else {
                onUploadCompleted('upload');
                setvosImg('');
            }
        },
        onError: () => onUploadError('upload'),
        refetchQueries: [
            {
                query: GET_VOS_UPLOAD_IMAGES,
                variables: {
                    input: {
                        jobId: jobId,
                        year: selectedYear,
                        monthId: selectedMonth,
                        weekId: -1,
                    },
                },
            },
        ],
    });

    const [deletejobImage, { loading: deleteLoading }] = useMutation(DELETE_VOS_UPLOAD_IMAGE, {
        onCompleted: () => onCompleted('delete'),
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_VOS_UPLOAD_IMAGES,
                    variables: {
                        input: {
                            jobId: jobId,
                            year: selectedYear,
                            monthId: selectedMonth,
                            weekId: -1,
                        },
                    },
                },
            ];
        },
    });

    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { id: record.id } };
        await deletejobImage({ variables });
    }, []);

    const fileUpload = async (): Promise<void> => {
        // Check if an image already exists for the selected week
        const existingImageForWeek = images.find((image) => {
            const weekNumber = selectedWeek?.toString();
            if (image.week && weekNumber) {
                return image.week.split('(')[0].split(' ')[1] === weekNumber;
            }
            return false;
        });
        if (existingImageForWeek) {
            toast.error('Vos form already exists for the selected week.', toastOpt);
            return;
        }

        const reader = new FileReader();
        const file = vosImg;
        reader.onloadend = async () => {
            const imageName = `Job-${jobId}-${file.name}`;
            const imageData = reader.result ? reader.result.toString().replace(/^data:(.*,)?/, '') : '';
            const extension = file.name.split('.').pop();

            const uploadImageToLoadInput: VosUploadImageInput = {
                jobId: jobId,
                year: selectedYear,
                monthId: selectedMonth,
                weekId: selectedWeek,
                uploadImageToJob: {
                    imageName: imageName,
                    imageData: imageData,
                    name: extension.toUpperCase(),
                },
            };
            uploadImageMutation({
                variables: { input: uploadImageToLoadInput },
            });
        };

        reader.readAsDataURL(file);
    };

    const uploadButtonDisabled = (): boolean => {
        if (!canUploadVos) return true;
        if (!vosImg) return true;
        if (!selectedWeek || selectedWeek === -1) return true;
        if (!canViewEditVos) return true;
        if (mutationLoading) return true;
        if (vosImg.name.length > 0) {
            return false;
        }
        return true;
    };

    const {
        data: jobNameData,
        loading: jobNameLoading,
        error: jobNameError,
    } = useQuery<GetFullJobProfile>(GET_JOB_PROFILE, {
        variables: {
            jobId: jobId,
        },
        skip: !jobId || jobId === 0,
    });
    let jobName = '';
    if (jobNameData && jobNameData.getJobProfile) {
        jobName = jobNameData.getJobProfile.jobDetails?.jobName ?? '';
    }
    document.title = jobName;

    if (error) {
        return <Error error={error} />;
    }

    if (!canViewEditVos) {
        return (
            <div className="Vos-Container-For-Tab">
                <div className="Vos-Header-Container">
                    <div>VOS: {jobName}</div>
                    <JobMenuItems jobId={jobId.toString()} menu="vosForTab" />
                </div>

                <div className="NoResult-Container VosForTab-Unauth-Container">
                    <div className="NoResult-First-Title-Secondary">Unauthorized</div>
                    <div className="NoResult-Second-Title-Secondary">You do not have permission to view this screen</div>
                </div>
            </div>
        )
    }

    return (
        <div className="Vos-Container-For-Tab">
            <ToastContainer />
            {loading || mutationLoading || deleteLoading ? (
                <div className="loading-icon">
                    <span>
                        <Loading />
                    </span>
                </div>
            ) : (
                ''
            )}
            <div className="Vos-Header-Container">
                <div>VOS: {jobName}</div>
                <JobMenuItems jobId={jobId.toString()} menu="vosForTab" />
            </div>
            <div className="Vos-Container-Tab">
                <div className="Vos-DropDown-Tab">
                    <div className="Vos-DropDown-Container-For-Tab">
                        <SearchableDropdown
                            dropdownOptions={years}
                            id="year-selector"
                            label="YEAR"
                            value={selectedYear}
                            onSelect={(eventKey: string | null) => {
                                handleYearSelect(eventKey);
                            }}
                        />
                    </div>

                    <div className="Vos-DropDown-Container-For-Tab">
                        <SearchableDropdown
                            dropdownOptions={months}
                            id="month-selector"
                            label="MONTH"
                            value={months.find((m) => m.id === selectedMonth)?.name ?? ''}
                            onSelect={(eventKey: string | null) => {
                                handleMonthSelect(eventKey);
                            }}
                        />
                    </div>
                    <div className="Vos-DropDown-Container-For-Tab">
                        <SearchableDropdown
                            dropdownOptions={weeks}
                            id="week-selector"
                            label="WEEK"
                            value={
                                selectedWeek !== null
                                    ? weeks.find((week) => week.id === selectedWeek)?.name ?? ''
                                    : defaultWeekOption.name
                            }
                            onSelect={(week: string | null) => handleWeekSelect(week)}
                        />
                    </div>
                </div>

                {
                    canUploadVos && <div className="Vos-Upload-Button-Container-For-Tab">
                        <div className="Row Vos-FileBrowser-Background-For-Tab">
                            <label className="Vos-FileBrowser-Label">
                                {vosImg ? vosImg.name : 'Click here to choose file'}
                                <input
                                    type="file"
                                    onChange={(event) => handleLoadFileChange(event)}
                                    className="Vos-FileBrowser-Input"
                                />
                            </label>
                            <button
                                disabled={uploadButtonDisabled()}
                                onClick={fileUpload}
                                className={`Vos-Upload-Button-For-Tab`}
                            >
                                Upload
                            </button>
                        </div>
                    </div>
                }
            </div>
            <br></br>
            <div>
                <div className="ImageList-Container-For-Tab">
                    <div className="ImagesList">
                        <div className="ImageList-Bottom-Container">
                            <Col>
                                <div className="ImageItem-List-Header">Week</div>
                            </Col>

                            <Col md={4}>
                                <div className="ImagesList">
                                    <div className="ImageItem-List-Header">Attachment</div>
                                </div>
                            </Col>
                            <Col>
                                <div className="ImageItem-List-Header">Received Date</div>
                            </Col>

                            <div className="ImageItem-List-Header">Uploaded By</div>
                            <div className="ImageItem-List-Header">Action</div>
                        </div>

                        {
                            images.length > 0 && (
                                <VosImageList
                                    images={images}
                                    canEditJobs={canViewEditVos}
                                    isLoading={loading}
                                    onDelete={onDelete}
                                />
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VosForTab;
